<template>
  <div v-if="visible" class="passkey-overlay" @click.self="snooze">
    <div class="passkey-prompt">
      <div class="passkey-prompt__icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linecap="round"
        >
          <path d="M12 11v4" />
          <path d="M8.6 9.5a4 4 0 0 1 6.8 0" />
          <path d="M6.2 7.6a7 7 0 0 1 11.6 0" />
          <path d="M9.5 13.5a2.5 2.5 0 0 1 5 0c0 2.2-.6 3.6-1.3 4.9" />
          <path d="M12 16.8c-.3 1-.8 1.9-1.5 2.7" />
        </svg>
      </div>
      <div class="passkey-prompt__body">
        <template v-if="done">
          <div class="passkey-prompt__title">הכל מוכן! 🎉</div>
          <div class="passkey-prompt__text">
            מהכניסה הבאה אפשר להתחבר עם טביעת אצבע — בלי סיסמה ובלי SMS
          </div>
        </template>
        <template v-else>
          <div class="passkey-prompt__title">להתחבר בלי סיסמה?</div>
          <div class="passkey-prompt__text">
            הפעילו כניסה עם טביעת אצבע / Face ID מהמכשיר הזה
          </div>
          <div class="passkey-prompt__actions">
            <button class="btn btn--blue" :disabled="busy" @click="enable">
              <span v-if="busy">רגע…</span>
              <span v-else>הפעלה</span>
            </button>
            <button class="btn passkey-prompt__later" @click="snooze">
              אחר כך
            </button>
          </div>
        </template>
      </div>
      <button class="passkey-prompt__close" aria-label="close" @click="snooze">
        ✕
      </button>
    </div>
  </div>
</template>

<script>
import { passkeysSupported, createPasskey } from '@/helpers/webauthn';

const SNOOZE_KEY = 'passkey_prompt_snoozed_until';
const SNOOZE_DAYS = 14;

function readSnooze() {
  try {
    return Number(localStorage.getItem(SNOOZE_KEY) || 0);
  } catch (e) {
    return 0;
  }
}

export default {
  name: 'PasskeyPrompt',
  data() {
    return {
      visible: false,
      busy: false,
      done: false
    };
  },
  async mounted() {
    if (!passkeysSupported()) {
      return;
    }
    if (readSnooze() > Date.now()) {
      return;
    }
    try {
      const { data } = await axios.get('/webauthn/credentials');
      if (!data.length) {
        this.visible = true;
      }
    } catch (e) {
      // No session or endpoint hiccup — a promo banner must never
      // bother anyone with errors.
    }
  },
  methods: {
    async enable() {
      if (this.busy) {
        return;
      }
      this.busy = true;
      try {
        const { data: options } = await axios.get('/webauthn/register-options');
        const credential = await createPasskey(options);
        await axios.post('/webauthn/register', {
          credential,
          name: this.deviceName()
        });
        this.done = true;
        setTimeout(() => {
          this.visible = false;
        }, 6000);
      } catch (e) {
        // User closed the biometric prompt or it failed — just hide,
        // they can always enable it later from the prompt or settings.
        this.snooze();
      } finally {
        this.busy = false;
      }
    },
    snooze() {
      // Storage may be blocked (private mode, site data disabled): the
      // prompt must still close, so never let that throw.
      try {
        localStorage.setItem(
          SNOOZE_KEY,
          String(Date.now() + SNOOZE_DAYS * 24 * 3600 * 1000)
        );
      } catch (e) {
        // ignore — the prompt simply shows again next time
      }
      this.visible = false;
    },
    deviceName() {
      const ua = navigator.userAgent;
      if (/iPhone|iPad/.test(ua)) {
        return 'iPhone';
      }
      if (/Android/.test(ua)) {
        return 'Android';
      }
      if (/Macintosh/.test(ua)) {
        return 'Mac';
      }
      if (/Windows/.test(ua)) {
        return 'Windows';
      }

      return null;
    }
  }
};
</script>

<style lang="scss" scoped>
.passkey-overlay {
  position: fixed;
  // `inset` is unknown to iOS Safari < 14.5 — spell it out.
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1200;
  background: rgba(20, 32, 45, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.passkey-prompt {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(15, 26, 38, 0.25);
  padding: 30px 28px 26px;
  direction: rtl;

  &__icon {
    width: 62px;
    height: 62px;
    border-radius: 50%;
    background: #e8f0fe;
    color: #4285f4;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;

    svg {
      width: 34px;
      height: 34px;
    }
  }

  &__title {
    font-size: 19px;
    font-weight: 700;
    margin-bottom: 7px;
  }

  &__text {
    font-size: 14px;
    color: #6b7a89;
    line-height: 1.55;
  }

  &__actions {
    display: flex;
    gap: 10px;
    margin-top: 20px;
    justify-content: center;

    .btn {
      padding: 9px 26px;
    }
  }

  &__later {
    background: transparent;
    color: #6b7a89;

    &:hover {
      color: #2c3e50;
    }
  }

  &__close {
    position: absolute;
    top: 10px;
    left: 12px;
    background: none;
    border: none;
    color: #b6c1cc;
    cursor: pointer;
    font-size: 13px;

    &:hover {
      color: #6b7a89;
    }
  }
}
</style>
